"use client";

import { type InfiniteData, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FiImage } from "react-icons/fi";
import { LuLoader2 } from "react-icons/lu";
import Image from "next/image";
import { Button, Input, Label } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { queryClient } from "@/config/react-query";
import { toast } from "@/lib/use-toast";
import { AttachmentDayPage, NoteAttachment } from "@/types/notes";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { updateAttachmentPortalSharing } from "@/api/provider/scribe/notes";
export interface ShareToPortalAttachment {
  id: string;
  name?: string;
  thumbnailUrl?: string;
  visibleToPatient?: boolean;
  patientCaption?: string | null;
}
interface ShareToPortalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: ShareToPortalAttachment | null;
}
export default function ShareToPortalModal({
  open,
  onOpenChange,
  attachment
}: ShareToPortalModalProps) {
  const [caption, setCaption] = useState("");
  const isShared = !!attachment?.visibleToPatient;

  // Seed the caption from the existing patient caption, falling back to the
  // photo's name so staff have a sensible default they can edit or clear.
  // Keyed on the attachment id (not the object) so a parent re-render — e.g. a
  // background query refetch handing us a new object literal — doesn't wipe out
  // whatever the user is currently typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && attachment) {
      setCaption(attachment.patientCaption ?? attachment.name ?? "");
    }
  }, [open, attachment?.id]);

  // Write the new sharing state straight into the cache for this attachment id
  // instead of invalidating. Invalidation triggers a refetch that can hit a
  // lagging read replica and momentarily paint the pre-write state back; seeding
  // the cache keeps every gallery grid instantly and correctly in sync off the
  // write we already know succeeded.
  const seedSharingIntoCache = (visibleToPatient: boolean) => {
    if (!attachment) return;
    const id = attachment.id;
    const nextCaption = visibleToPatient ? caption.trim() || null : undefined;
    const patch = (a: NoteAttachment): NoteAttachment => a.id === id ? {
      ...a,
      visibleToPatient,
      // Only overwrite the caption when sharing; unsharing preserves it.
      ...(nextCaption !== undefined ? {
        patientCaption: nextCaption
      } : {})
    } : a;

    // Gallery grids (patient and global) are infinite day-page queries.
    queryClient.setQueriesData<InfiniteData<AttachmentDayPage | null>>({
      queryKey: [...noteQueryKeys.all, "attachments-infinite"]
    }, old => old ? {
      ...old,
      pages: old.pages.map(page => page ? {
        ...page,
        data: page.data.map(patch)
      } : page)
    } : old);

    // Per-note attachment lists.
    queryClient.setQueriesData<NoteAttachment[] | null>({
      queryKey: [...noteQueryKeys.all, "attachments"]
    }, old => Array.isArray(old) ? old.map(patch) : old);
  };
  const shareMutation = useMutation({
    mutationFn: async (visibleToPatient: boolean) => {
      if (!attachment) return;
      await updateAttachmentPortalSharing(attachment.id, visibleToPatient, visibleToPatient ? caption.trim() : undefined);
    },
    onSuccess: (_data, visibleToPatient) => {
      seedSharingIntoCache(visibleToPatient);
      toast({
        title: visibleToPatient ? "Shared" : "Removed",
        description: visibleToPatient ? "This photo is now visible in the patient portal." : "This photo is no longer visible in the patient portal.",
        variant: "success"
      });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update patient portal sharing. Please try again.",
        variant: "error"
      });
    }
  });
  const isPending = shareMutation.isPending;
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isShared ? "Patient portal sharing" : "Share to patient portal"}
          </DialogTitle>
          <DialogDescription>
            {isShared ? "This photo is shared with the patient. Update the label or stop sharing it." : "Share this photo with the patient in their portal. Add an optional label they'll see."}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex items-start gap-4">
            <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border bg-muted">
              {attachment?.thumbnailUrl ? <Image src={attachment.thumbnailUrl} alt={attachment.name || "Photo"} fill className="object-cover" unoptimized /> : <div className="flex h-full w-full items-center justify-center">
                  <FiImage className="h-6 w-6 text-foreground-muted" />
                </div>}
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="portal-caption">Label (optional)</Label>
              <Input id="portal-caption" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Before treatment" autoFocus disabled={isPending} />
              <p className="text-caption text-foreground-secondary">
                Shown next to the photo in the patient portal.
              </p>
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="sm:justify-between">
          {isShared ? <Button className="text-status-error-text" variant="ghost" onClick={() => shareMutation.mutate(false)} disabled={isPending}>
              Stop sharing
            </Button> : <span />}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={() => shareMutation.mutate(true)} disabled={isPending}>
              {isPending ? <>
                  <LuLoader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </> : isShared ? "Update" : "Share"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}